<template>
  <div class="avatars-form">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
      :loading="loading"
    >
      <template #actions v-if="!isOnlyView">
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="action"
          >
            <span class="px-2">{{ avatarsCategoryId ? 'Save' : 'Add'}}</span>
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="d-flex align-items-center justify-content-between">
        <FormTitle title="General info" />
        <a-button
          v-if="isOnlyView"
          size="large"
          :disabled="!editPermission"
          @click="goToEdit"
        >
          <span class="px-2">Edit</span>
        </a-button>
      </div>
      <a-form-model :model="form" ref="form">
        <a-row class="d-flex">
          <Readonly
            :show="isOnlyView && !!form.name"
            title="Category name"
            :value="form.name"
            style="max-width: 270px; align-self: flex-start;"
            class="mb-3"
            :class="{'mb-4' : isOnlyView}"
          >
            <a-form-model-item
              prop="name"
              :colon="false"
              :rules="[{ required: true, message: 'Category name is required', trigger: ['blur', 'change'] }]"
              class="label-with-tooltip"
              style="max-width: 270px"
            >
              <template #label>
                <div>
                  <span class="mr-2 mb-0 font-weight-bold" style="line-height: initial;">Category name</span>
                  <a-tooltip
                    placement="top"
                    arrow-point-at-center
                    :overlayStyle="{ 'white-space': 'normal' }"
                    :get-popup-container="getPopupContainer"
                  >
                    <template slot="title">
                      <span class="font-weight-light">This name will be used to pick the avatar set for referral program</span>
                    </template>
                    <a-icon class="d-flex" type="info-circle" />
                  </a-tooltip>
                </div>
              </template>
              <a-input
                v-model="form.name"
                style="width: 270px;"
                placeholder="Category name"
                size="large"
                allow-clear
                :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                :disabled="loading || isOnlyView"
              />
            </a-form-model-item>
          </Readonly>
        </a-row>
        <FormTitle title="Avatars" tooltipText="This name will be used to pick the avatar set for referral program" />
        <FormSectionSeparator icon="folder-open" title="Uploaded avatars" />
        <a-form-model-item
          v-if="!isOnlyView"
          prop="media"
          :colon="false"
          :rules="[
            {
              validator: validateMedia,
              trigger: ['change'],
            }
          ]"
        >
          <a-tooltip
            placement="top"
            arrow-point-at-center
            :overlayStyle="{ 'white-space': 'normal' }"
            :get-popup-container="getPopupContainer"
          >
            <template slot="title">
              <span class="font-weight-light">File size 88x88 px<br>(jpg, jpeg, png, webp)</span>
            </template>
            <a-upload
              name="file"
              :multiple="true"
              :show-upload-list="false"
              accept=".png,.jpg,.jpeg,.webp"
              action="#"
              class="d-block my-2 w-fit"
              :disabled="loading || imageLoading"
              :before-upload="beforeUpload"
              @change="imgChange"
            >
              <a-button> <a-icon type="upload" /> Upload </a-button>
            </a-upload>
          </a-tooltip>
        </a-form-model-item>
        <div v-if="!form.media.length" class="my-5">
          <Empty>
            <span slot="description" class="text-default">There are no images uploaded yet.</span>
          </Empty>
        </div>
        <UploadedImages
          v-else
          :media="form.media"
          :editPermission="editPermission"
          :deletePermission="deletePermission"
          @update="updateMedia"
          @delete="deleteMedia"
        />
      </a-form-model>
    </MainContainer>
    <div v-if="!isOnlyView && isEditMode && deletePermission" class="mt-3">
      <div
        class="d-flex align-items-center text-danger cursor-pointer hover-underline"
        style="width: fit-content;"
        @click="toggleShowDeleteModal(true)"
      >
        <a-icon type="delete" class="mr-2" />
        <span>Delete category</span>
      </div>
    </div>
    <DefaultModal
      :show="showDeleteModal"
      title="Delete Avatar category"
      text="Are you sure you want to delete this category?"
      description="All associated data will be lost"
      :actionButton="{
        text: 'Delete',
        type: 'danger',
      }"
      :loading="loading"
      @close="toggleShowDeleteModal"
      @action="removeAvatars"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import FormSectionSeparator from '@/components/custom/form/FormSectionSeparator.vue'
import DefaultModal from '@/components/custom/modals/defaultModal'
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import UploadedImages from '@/views/apps/referral-program/avatars/form/UploadedImages.vue'
import { Empty } from 'ant-design-vue'
import { beforeUpload, getBase64 } from '@/composables/useUploadImage.js'
import { getRandomId } from '@/composables/useGetRandomId.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    FormTitle,
    FormSectionSeparator,
    UploadedImages,
    Empty,
    Readonly,
    DefaultModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    avatarsCategoryId: null,
    currentAvatarsCategory: null,
    form: {
      name: '',
      media: [],
    },
    loading: false,
    imageLoading: false,
    showDeleteModal: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isOnlyView() {
      return this.$route.name === 'view-avatars'
    },
    isEditMode() {
      return !!this.avatarsCategoryId
    },
    breadcrumbs() {
      if (this.currentAvatarsCategory) {
        if (this.isOnlyView) {
          return [
            { name: 'Avatars', link: '/referral/avatars' },
            { name: 'View avatars', link: undefined },
          ]
        }
        return [
          { name: 'Avatars', link: '/referral/avatars' },
          { name: this.currentAvatarsCategory?.name, link: `/referral/avatars/${this.currentAvatarsCategory.id}/view` },
          { name: 'Edit avatars', link: undefined },
        ]
      }
      return [
        { name: 'Avatars', link: '/referral/avatars' },
        { name: 'Create avatars', link: undefined },
      ]
    },
    currentMediaIds() {
      if (this.form.media?.length) {
        return this.form.media.map(media => media.uniqueId)
      }
      return []
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    goToEdit() {
      this.$router.push(`/referral/avatars/${this.avatarsCategoryId}`)
    },
    async validateMedia(_, value) {
      if (!value || value.length === 0) {
        return await Promise.reject(new Error('At least one media file is required'))
      }
      return await Promise.resolve()
    },
    async beforeUpload(file) {
      return await beforeUpload(this, file, 2, 88)
    },
    imgChange(info) {
      if (info.file.status === 'uploading') {
        this.imageLoading = true
        return
      }

      info.file.status = 'done'

      const image = {
        image: info.file.originFileObj,
      }

      getBase64(info.file.originFileObj, imageUrl => {
        image.src = imageUrl
        image.uniqueId = getRandomId(this.currentMediaIds)
        this.form.media.push(image)
        this.imageLoading = false
        this.$refs.form.validateField('media')
      })
    },
    updateMedia(newMedia) {
      this.form.media = newMedia
    },
    deleteMedia(index) {
      this.form.media.splice(index, 1)
    },
    toggleShowDeleteModal(status = false) {
      this.showDeleteModal = status
    },
    action() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          this.$notification.error({
            message: 'Please fill in all required fields',
          })
          return false
        }

        const data = this.getPayloadData()
        if (this.isEditMode) {
          this.updateAvatars(data)
          return
        }
        this.createAvatars(data)
      })
    },
    getPayloadData() {
      const formData = new FormData()

      if (this.isEditMode) {
        formData.append('_method', 'PUT')
      }

      formData.append('name', this.form.name)

      this.form.media.forEach((el, index) => {
        if (el?.image instanceof File) {
          formData.append(`media[${index}]`, el.image)
        } else {
          formData.append(`media[${index}]`, el.id)
        }
      })

      return formData
    },
    async createAvatars(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/avatar-packages`
        const response = await apiClient.post(url, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Avatar category successfully created',
            description: '',
          })
          this.$router.push('/referral/avatars')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async updateAvatars(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/avatar-packages/${this.avatarsCategoryId}`
        const response = await apiClient.post(url, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Avatar category successfully updated',
            description: '',
          })
          this.$router.push('/referral/avatars')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async removeAvatars() {
      try {
        this.loading = true
        const url = `${referralApiUrl}/avatar-packages/${this.avatarsCategoryId}`
        const response = await apiClient.delete(url)
        if (response?.status >= 200 && response?.status < 300) {
          this.$notification.success({
            message: 'Avatar category successfully removed',
            description: '',
          })
          this.$router.push('/referral/avatars')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async getAvatarsParams() {
      try {
        const url = `${referralApiUrl}/avatar-packages/${this.avatarsCategoryId}`

        const response = await apiClient.get(url)
        if (response?.data?.data) {
          const { data } = response.data
          this.currentAvatarsCategory = data
          this.form.name = data.name
          data.media.forEach(media => {
            const filledItem = {
              id: media.id,
              src: media.src,
              blurhash: media.blurhash,
              uniqueId: media.id,
            }
            this.form.media.push(filledItem)
          })
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
  },
  async mounted() {
    this.avatarsCategoryId = this.$route.params.id

    try {
      this.loading = true
      if (this.avatarsCategoryId) {
        await this.getAvatarsParams()
      }
    } finally {
      this.loading = false
    }
  },
}
</script>

<style lang="scss">
.avatars-form {
  &__handle {
    transition: width 2s ease;
    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      align-self: flex-start;
      height: 46px;
      width: 20px;
      min-width: 20px;
      transition: all .3s ease;
      &__hide {
        width: 0;
        min-width: 0;
      }
      &__show {
        width: 20px;
        min-width: 20px;
      }
    }
  }
}
</style>
